import { useEffect, useMemo, useState } from "preact/hooks";
import { defineMessages, getLocale, intlLocale, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";
import { TRANSLATION_LANGUAGES } from "../translate.ts";

const messages = defineMessages(
  {
    title: "Traduction",
    into: "Traduire en",
    translating: "Traduction…",
    failed: "La traduction n’a pas abouti : réessayez dans un instant.",
  },
  {
    title: "Translation",
    into: "Translate into",
    translating: "Translating…",
    failed: "The translation did not come through: try again in a moment.",
  },
);

interface Props {
  text: string;
  /** La langue du passage (deux lettres ; "" quand on ne la connaît pas). */
  source: string;
  /** La langue de départ de la traduction ; la liste permet d'en choisir une autre. */
  target: string;
  /** La traduction par le serveur (Mistral). */
  translateOnServer: (text: string, source: string, target: string, signal: AbortSignal) => Promise<string | null>;
}

/** « Traduire » dans la fenêtre d'« Explique-moi » : la langue d'arrivée, et la traduction du serveur en gras. */
export function TranslationResult({ text, source, target: initialTarget, translateOnServer }: Props) {
  const t = useT(messages);
  const [target, setTarget] = useState(initialTarget);
  const [state, setState] = useState<{ loading: boolean; translation: string | null }>({ loading: true, translation: null });
  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, translation: null });
    void (async () => {
      const translation = await translateOnServer(text, source, target, controller.signal);
      if (!controller.signal.aborted) setState({ loading: false, translation });
    })();
    return () => controller.abort();
    // La fonction du serveur est celle de l'hôte : une nouvelle à chaque rendu, le même serveur.
  }, [text, source, target]);
  const locale = getLocale();
  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames([intlLocale(locale)], { type: "language" });
    } catch {
      return null;
    }
  }, [locale]);
  const languages = TRANSLATION_LANGUAGES.filter((code) => code !== source);
  const options = languages.includes(target as (typeof languages)[number]) ? languages : [target, ...languages];
  return (
    <section class="msb-translation" aria-label={t("title")}>
      <div class="msb-translation__head">
        <Icon name="translate" size={22} />
        <label class="msb-translation__target">
          <span>{t("into")}</span>
          <select value={target} onChange={(event) => setTarget((event.target as HTMLSelectElement).value)}>
            {options.map((code) => (
              <option key={code} value={code}>
                {names?.of(code) ?? code}
              </option>
            ))}
          </select>
        </label>
      </div>
      {state.loading ? (
        <p role="status">{t("translating")}</p>
      ) : state.translation ? (
        <p class="msb-definition__text msb-translation__text" lang={target}>
          {state.translation}
        </p>
      ) : (
        <p class="msb-translation__note">{t("failed")}</p>
      )}
    </section>
  );
}
